import { useEffect, useRef, useState } from "react";

// Keep in sync with the exit animation duration in index.css
const EXIT_MS = 180;

const prefersReducedMotion = () =>
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

// While active: locks page scrolling and closes on Escape. Restores both on close/unmount.
export function useScrollLock(isActive, onClose) {
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    if (!isActive) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onCloseRef.current?.();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isActive]);
}

// Keeps a modal mounted until its exit animation finishes.
// isClosing is true while the exit animation plays.
export default function useModalTransition(isOpen, onClose) {
  const [isMounted, setIsMounted] = useState(isOpen);
  // Mount in the same render that opens, so the enter animation starts without a blank frame
  if (isOpen && !isMounted) setIsMounted(true);

  useEffect(() => {
    if (isOpen || !isMounted) return;
    const timer = setTimeout(() => setIsMounted(false), prefersReducedMotion() ? 0 : EXIT_MS);
    return () => clearTimeout(timer);
  }, [isOpen, isMounted]);

  useScrollLock(isOpen, onClose);

  return { isMounted, isClosing: isMounted && !isOpen };
}
